import { computed, reactive, shallowRef, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type ShallowRef } from 'vue'
import { initApiService } from '@/services/init'
import { logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * How long an object URL outlives its last user. Navigating from the cliënt list into a
 * dossier unmounts the row before the header mounts, so revoking at once would fetch the
 * same photo twice within one click.
 */
export const RELEASE_GRACE_MS = 30_000

interface AvatarEntry {
  objectUrl: ShallowRef<string | null>
  users: number
  requested: boolean
  /** Bumped on every reset, so a response that arrives after one is discarded. */
  generation: number
  releaseTimer: ReturnType<typeof setTimeout> | null
}

/**
 * The photo of each cliënt, held in memory only. The endpoint answers `no-store` and the
 * URL stays the same across a re-upload, so neither the browser cache nor the URL can
 * tell a new photo from the old one: this map is the cache, and invalidation keeps it current.
 */
const entries = new Map<string, AvatarEntry>()

/**
 * What an upload or delete in this tab learned about whether a cliënt has a photo. It
 * overrides the `avatarUrl` a component was rendered with, because the dossier header
 * and sidebar hold their own copy of the cliënt that the upload control cannot reach.
 */
const knownPresence = reactive(new Map<string, boolean>())

function revoke(entry: AvatarEntry) {
  if (entry.objectUrl.value) URL.revokeObjectURL(entry.objectUrl.value)
  entry.objectUrl.value = null
}

function acquire(clientId: string): AvatarEntry {
  let entry = entries.get(clientId)
  if (!entry) {
    entry = { objectUrl: shallowRef(null), users: 0, requested: false, generation: 0, releaseTimer: null }
    entries.set(clientId, entry)
  }
  if (entry.releaseTimer) {
    clearTimeout(entry.releaseTimer)
    entry.releaseTimer = null
  }
  entry.users++
  return entry
}

function release(clientId: string, entry: AvatarEntry) {
  entry.users--
  if (entry.users > 0) return
  entry.releaseTimer = setTimeout(() => {
    revoke(entry)
    entry.generation++
    if (entries.get(clientId) === entry) entries.delete(clientId)
  }, RELEASE_GRACE_MS)
}

async function load(clientId: string, entry: AvatarEntry) {
  if (entry.requested) return
  entry.requested = true
  const generation = entry.generation
  try {
    const response = await initApiService().clientAvatar.getClientAvatar(clientId, { responseType: 'blob' })
    if (generation !== entry.generation) return
    revoke(entry)
    entry.objectUrl.value = URL.createObjectURL(response.data as unknown as Blob)
  } catch (err: unknown) {
    // The initials stay. A photo that is gone or not visible to this user is no error to show.
    logUnexpectedFailure('Failed to load client avatar:', err)
  }
}

/**
 * Drops the cached photo after an upload or delete, so every place showing the cliënt
 * shows the new state without a reload.
 */
export function invalidateClientAvatar(clientId: string, hasPhoto: boolean) {
  knownPresence.set(clientId, hasPhoto)
  const entry = entries.get(clientId)
  if (!entry) return
  revoke(entry)
  entry.generation++
  entry.requested = false
  if (hasPhoto && entry.users > 0) void load(clientId, entry)
}

/**
 * The cliënt's photo as an object URL, fetched with the signed-in user's token — a plain
 * `<img src>` on the API path carries none. `null` while there is no photo, while it
 * loads, and when it cannot be loaded; the caller shows the initials then.
 *
 * @param enabled - Defers the fetch, e.g. until a list row scrolls into view.
 */
export function useClientAvatar(
  clientId: MaybeRefOrGetter<string | null | undefined>,
  avatarUrl: MaybeRefOrGetter<string | null | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true
): { src: ComputedRef<string | null> } {
  const entry = shallowRef<AvatarEntry | null>(null)

  const wantedClientId = computed(() => {
    const id = toValue(clientId)
    if (!id || !toValue(enabled)) return null
    const hasPhoto = knownPresence.get(id) ?? !!toValue(avatarUrl)
    return hasPhoto ? id : null
  })

  watch(wantedClientId, (id, _previous, onCleanup) => {
    entry.value = null
    if (!id) return
    const acquired = acquire(id)
    entry.value = acquired
    void load(id, acquired)
    onCleanup(() => release(id, acquired))
  }, { immediate: true })

  return { src: computed(() => entry.value?.objectUrl.value ?? null) }
}

/** Empties the cache, so no cliënt's photo outlives the session that loaded it. */
export function resetClientAvatarCache() {
  for (const entry of entries.values()) {
    if (entry.releaseTimer) clearTimeout(entry.releaseTimer)
    revoke(entry)
  }
  entries.clear()
  knownPresence.clear()
}
